import { removeAuth0Prefix } from '@paintscout/util'
import { useMemo } from 'react'
import { useUsers } from '../providers/UsersProvider'

type ActiveUser = { user_id?: string; app_metadata?: { active?: boolean } }

/**
 * Builds a prefix-stripped `userId -> active` lookup from a users list.
 *
 * Centralizes the "is this owner still active" rule used across avatar
 * surfaces. Missing users are simply absent from the map; callers treat an
 * absent entry as active (see `isUserActive`) to match the long-standing
 * ActivityAvatar default — we never want to grey out a cross-company or
 * legacy reference we can't resolve.
 */
export function buildUserActiveMap(users: ActiveUser[] = []): Map<string, boolean> {
  const map = new Map<string, boolean>()
  for (const u of users) {
    if (!u?.user_id) continue
    map.set(removeAuth0Prefix(u.user_id), u.app_metadata?.active ?? true)
  }
  return map
}

/**
 * Resolves active status for an owner id against a prebuilt map.
 * Defaults to `true` when the owner has no id or isn't in the map.
 */
export function isUserActive(activeMap: Map<string, boolean>, ownerId?: string | null): boolean {
  if (!ownerId) return true
  return activeMap.get(removeAuth0Prefix(ownerId)) ?? true
}

/**
 * Hook form for single-owner lookups inside components.
 *
 * Falls back to `true` when:
 *   - no owner is supplied (nothing to grey out)
 *   - the user cannot be found in the users list
 */
export function useOwnerActive(owner?: { id?: string } | null): boolean {
  const { users } = useUsers()
  const activeMap = useMemo(() => buildUserActiveMap(users as ActiveUser[]), [users])
  return isUserActive(activeMap, owner?.id)
}
